import React, { useState, useMemo, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Landmark, Plus, MapPin, Edit2, X, Check, Loader2, Wheat, Tractor, TreeDeciduous, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

const atividadeOptions = [
  { value: "soja", label: "Soja" },
  { value: "milho", label: "Milho" },
  { value: "cafe", label: "Café" },
  { value: "algodao", label: "Algodão" },
  { value: "cana", label: "Cana-de-açúcar" },
  { value: "bovino_corte", label: "Pecuária de Corte" },
  { value: "bovino_leite", label: "Pecuária Leiteira" },
  { value: "suino", label: "Suinocultura" },
  { value: "aves", label: "Avicultura" },
  { value: "hortifruti", label: "Hortifruti" },
  { value: "florestal", label: "Florestal" },
  { value: "outro", label: "Outro" },
];

export default function Propriedades() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    area_total: "",
    localizacao: "",
    tipo_atividade: [],
    car: ""
  });
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const queryClient = useQueryClient();
  const [userEmail, setUserEmail] = useState("");
  const [defaultPropId, setDefaultPropId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const u = await agrogestao.auth.me();
        const em = u?.email || "";
        setUserEmail(em);
        const key = em ? `default_propriedade_${em}` : 'default_propriedade';
        const d = typeof localStorage !== 'undefined' ? (localStorage.getItem(key) || "") : "";
        setDefaultPropId(d);
      } catch {}
    })();
  }, []);

  const { data: propriedades = [], isLoading } = useQuery({
    queryKey: ['propriedades'],
    queryFn: async () => {
      const u = await agrogestao.auth.me();
      if (!u?.email) return [];
      return agrogestao.entities.Propriedade.filter({ created_by: u.email }, '-created_date');
    }
  });

  const { data: licencas = [] } = useQuery({
    queryKey: ['licencas_props', propriedades.map(p => p.id)],
    queryFn: async () => {
      const ids = propriedades.map(p => p.id);
      if (ids.length === 0) return [];
      const { data, error } = await agrogestao.client
        .from('licencas')
        .select('id, propriedade_id, plano, status, valid_until')
        .in('propriedade_id', ids);
      if (error) return [];
      return data || [];
    },
    enabled: propriedades.length > 0
  });

  const licencaPorPropriedade = useMemo(() => {
    const map = {};
    licencas.forEach(l => {
      const key = l.propriedade_id;
      const atual = map[key];
      if (!atual) map[key] = l;
      else {
        const a = atual.valid_until ? new Date(atual.valid_until) : new Date(0);
        const b = l.valid_until ? new Date(l.valid_until) : new Date(0);
        if (b > a) map[key] = l;
      }
    });
    return map;
  }, [licencas]);

  const statusLicenca = (propId) => {
    const lic = licencaPorPropriedade[propId];
    if (!lic) return { label: 'Sem licença', color: 'bg-stone-200 text-stone-700', permitido: false };
    if (lic.status === 'bloqueado') return { label: 'Bloqueado', color: 'bg-red-100 text-red-700', permitido: false };
    const exp = lic.valid_until ? new Date(lic.valid_until) : null;
    const ativa = exp ? exp >= new Date() : true;
    if (ativa) {
      const label = exp
        ? `Ativo${lic.plano === 'trial' ? ' (trial)' : ''} até ${format(exp, 'dd/MM/yyyy')}`
        : `Ativo${lic.plano === 'trial' ? ' (trial)' : ''}`;
      return { label, color: 'bg-green-100 text-green-700', permitido: true };
    }
    return { label: `Vencido em ${exp ? format(exp, 'dd/MM/yyyy') : ''}`, color: 'bg-amber-100 text-amber-700', permitido: false };
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me();
      if (u?.email) {
        try {
          await agrogestao.client.from('users').upsert(
            { email: u.email, full_name: u.user_metadata?.full_name },
            { onConflict: 'email' }
          );
        } catch {}
      }
      const payload = { ...data, created_by: u?.email || null };
      const prop = await agrogestao.entities.Propriedade.create(payload);
      try {
        await agrogestao.functions.invoke('criarLicencaTrial', { propriedade_id: prop.id });
      } catch {
        try {
          const now = new Date();
          const until = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          await agrogestao.client.from('licencas').insert({ propriedade_id: prop.id, plano: 'trial', status: 'ativo', valid_until: until });
        } catch {}
      }
      return prop;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propriedades'] });
      resetForm();
    },
    onError: (err) => {
      const msg = String(err?.message || err || "Falha ao salvar");
      const tooLongMatch = msg.match(/value too long.*character varying\((\d+)\)/i);
      if (tooLongMatch) {
        setFormError("Alguns campos excedem o tamanho máximo permitido");
      } else {
        setFormError(msg);
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.Propriedade.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propriedades'] });
      resetForm();
    },
    onError: (err) => {
      const msg = String(err?.message || err || "Falha ao salvar");
      const tooLongMatch = msg.match(/value too long.*character varying\((\d+)\)/i);
      if (tooLongMatch) {
        setFormError("Alguns campos excedem o tamanho máximo permitido");
      } else {
        setFormError(msg);
      }
    }
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, motivo }) => agrogestao.entities.Propriedade.update(id, { inativa: true, inativada_em: new Date().toISOString(), inativada_motivo: motivo || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propriedades'] });
    }
  });

  const activateMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Propriedade.update(id, { inativa: false, inativada_em: null, inativada_motivo: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propriedades'] });
    }
  });

  const resetForm = () => {
    setFormData({ nome: "", area_total: "", localizacao: "", tipo_atividade: [], car: "" });
    setEditingId(null);
    setDialogOpen(false);
    setFormError("");
    setFieldErrors({});
  };

  const handleEdit = (prop) => {
    setFormData({
      nome: prop.nome || "",
      area_total: prop.area_total || "",
      localizacao: prop.localizacao || "",
      tipo_atividade: prop.tipo_atividade || [],
      car: prop.car || ""
    });
    setEditingId(prop.id);
    setDialogOpen(true);
    setFormError("");
    setFieldErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    const errs = {};
    const MAX_NOME = 150;
    const MAX_LOCALIZACAO = 150;
    const MAX_CAR = 50;
    if ((formData.nome || "").length > MAX_NOME) errs.nome = `Máximo de ${MAX_NOME} caracteres`;
    if ((formData.localizacao || "").length > MAX_LOCALIZACAO) errs.localizacao = `Máximo de ${MAX_LOCALIZACAO} caracteres`;
    if ((formData.car || "").length > MAX_CAR) errs.car = `Máximo de ${MAX_CAR} caracteres`;
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setFormError("Verifique os campos destacados: texto muito longo");
      return;
    }
    const data = {
      ...formData,
      area_total: formData.area_total ? parseFloat(formData.area_total) : null
    };
    
    if (editingId) {
      updateMutation.mutate({ id: editingId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const toggleAtividade = (value) => {
    setFormData(prev => ({
      ...prev,
      tipo_atividade: prev.tipo_atividade.includes(value)
        ? prev.tipo_atividade.filter(v => v !== value)
        : [...prev.tipo_atividade, value]
    }));
  };

  const handleSelectPropriedade = (prop) => {
    localStorage.setItem("propriedade_selecionada", prop.id);
    window.location.href = '/Dashboard';
  };

  const handleRenovarLicenca = (prop) => {
    localStorage.setItem("propriedade_selecionada", prop.id);
    window.location.href = '/Dashboard?action=pix';
  };

  const handleSetDefault = (prop) => {
    try {
      const key = userEmail ? `default_propriedade_${userEmail}` : 'default_propriedade';
      localStorage.setItem(key, prop.id);
      setDefaultPropId(prop.id);
    } catch {}
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800">Minhas Propriedades</h1>
          <p className="text-stone-600 mt-1">Gerencie suas fazendas e áreas produtivas</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => resetForm()}
              className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2"
            >
              <Plus className="w-5 h-5" />
              Nova Propriedade
            </Button>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Landmark className="w-5 h-5 text-[#40916C]" />
                {editingId ? "Editar Propriedade" : "Nova Propriedade"}
              </DialogTitle>
              <DialogDescription>
                Preencha os dados da propriedade
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-5 pt-4">
              <div className="space-y-2">
                <Label>Nome da Propriedade *</Label>
                <Input
                  placeholder="Ex: Fazenda São João"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  required
                  maxLength={150}
                  className={fieldErrors.nome ? "border-red-500 focus-visible:ring-red-500" : undefined}
                />
                {fieldErrors.nome && <div className="text-red-600 text-xs">{fieldErrors.nome}</div>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Área Total (hectares)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 150"
                    value={formData.area_total}
                    onChange={(e) => setFormData({...formData, area_total: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CAR</Label>
                  <Input
                    placeholder="Cadastro Ambiental"
                    value={formData.car}
                    onChange={(e) => setFormData({...formData, car: e.target.value})}
                    maxLength={50}
                    className={fieldErrors.car ? "border-red-500 focus-visible:ring-red-500" : undefined}
                  />
                  {fieldErrors.car && <div className="text-red-600 text-xs">{fieldErrors.car}</div>}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Localização</Label>
                <Input
                  placeholder="Cidade/Estado"
                  value={formData.localizacao}
                  onChange={(e) => setFormData({...formData, localizacao: e.target.value})}
                  maxLength={150}
                  className={fieldErrors.localizacao ? "border-red-500 focus-visible:ring-red-500" : undefined}
                />
                {fieldErrors.localizacao && <div className="text-red-600 text-xs">{fieldErrors.localizacao}</div>}
              </div>

              <div className="space-y-3">
                <Label>Atividades</Label>
                <div className="grid grid-cols-2 gap-2">
                  {atividadeOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.tipo_atividade.includes(opt.value)
                          ? 'border-[#40916C] bg-[#40916C]/5'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.tipo_atividade.includes(opt.value)}
                        onCheckedChange={() => toggleAtividade(opt.value)}
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formError && (
                <div className="text-red-600 text-sm">{formError}</div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingId ? "Atualizar" : "Cadastrar"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Empty State */}
      {propriedades.length === 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <div className="w-20 h-20 bg-[#40916C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Landmark className="w-10 h-10 text-[#40916C]" />
          </div>
          <h3 className="text-xl font-bold text-stone-800 mb-2">Nenhuma propriedade cadastrada</h3>
          <p className="text-stone-600 mb-6">Cadastre sua primeira fazenda para começar</p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2"
          >
            <Plus className="w-5 h-5" />
            Cadastrar Propriedade
          </Button>
        </div>
      )}

      {/* Properties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {propriedades.map((prop, index) => {
            const st = statusLicenca(prop.id);
            return (
            <motion.div
              key={prop.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: index * 0.1 }}
            >
          <Card className="bg-white border-stone-200 hover:shadow-lg transition-all overflow-hidden group">
            <div className="h-32 bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] relative">
              <div className="absolute inset-0 opacity-20">
                <TreeDeciduous className="absolute top-4 left-4 w-12 h-12 text-white" />
                <Wheat className="absolute bottom-4 right-4 w-8 h-8 text-white" />
                <Tractor className="absolute top-8 right-8 w-10 h-10 text-white" />
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-xl font-bold text-white truncate">{prop.nome}</h3>
                {prop.localizacao && (
                  <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {prop.localizacao}
                  </p>
                )}
              </div>
            </div>
            
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-stone-500">Licença</span>
                <Badge className={st.color}>{st.label}</Badge>
              </div>
              {prop.inativa && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-stone-500">Status</span>
                  <Badge className="bg-stone-200 text-stone-700">Inativa</Badge>
                </div>
              )}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-500">Área Total</span>
                  <span className="font-semibold text-stone-800">
                    {prop.area_total ? `${prop.area_total} ha` : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-500">Padrão</span>
                  <div className="flex items-center gap-2">
                    {defaultPropId === String(prop.id) ? (
                      <Badge className="bg-indigo-100 text-indigo-700">
                        <Star className="w-3 h-3 mr-1" />
                        Padrão
                      </Badge>
                    ) : (
                      <Button size="xs" variant="outline" onClick={() => handleSetDefault(prop)}>Definir como padrão</Button>
                    )}
                  </div>
                </div>
                
                {prop.tipo_atividade?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {prop.tipo_atividade.slice(0, 3).map((at) => (
                      <span 
                        key={at}
                        className="px-2 py-1 bg-[#40916C]/10 text-[#40916C] text-xs rounded-full font-medium"
                      >
                        {atividadeOptions.find(o => o.value === at)?.label || at}
                      </span>
                    ))}
                    {prop.tipo_atividade.length > 3 && (
                      <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded-full">
                        +{prop.tipo_atividade.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-5 pt-4 border-t border-stone-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEdit(prop)}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
                  onClick={() => handleSelectPropriedade(prop)}
                  disabled={!st.permitido || !!prop.inativa}
                >
                  Acessar
                </Button>
                {(!st.permitido) && (
                  <Button
                    size="sm"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleRenovarLicenca(prop)}
                  >
                    Renovar Licença
                  </Button>
                )}
                {prop.inativa ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => activateMutation.mutate(prop.id)}
                    disabled={activateMutation.isPending}
                  >
                    Ativar
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => archiveMutation.mutate({ id: prop.id })}
                    disabled={archiveMutation.isPending}
                  >
                    Arquivar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
            </motion.div>
          ); })}
        </AnimatePresence>
      </div>
    </div>
  );
}
